import { AddFamilyMemberHistoryDialog } from '@/components/molecules/AddFamilyMemberHistoryDialog';
import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useCompleteRecordList } from '@liza/ui/hooks/useCompleteRecordList';
import { useTRPC, useTRPCClient } from '@/utils/trpc';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { FamilyMemberHistoryList } from '@liza/ui/components/records/FamilyMemberHistoryList';
import {
  defaultFilters,
  RecordsListControls,
  type RecordsListFilters,
} from '@liza/ui/components/records/RecordsListControls';
import { useRecordPromotionStatus } from '@liza/ui/hooks/useRecordPromotionStatus';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  selectedId?: string;
};

export const FamilyHistory = ({ onViewChange, patient, selectedId }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const listOptions = {
    ownership: filters.ownership,
    searchParams: { 'code:text': filters.searchQuery },
    status: filters.status,
  };

  // The list merges the resources describing one relative and pivots by condition, so it needs the
  // whole list first.
  const {
    items: familyMemberHistories,
    query,
    isComplete,
  } = useCompleteRecordList({
    queryKey: trpc.patient.familyMemberHistory.list.queryKey({
      medplumPatientId: patient.medplumId,
      options: listOptions,
    }),
    queryFn: async ({ offset, count }) => {
      const res = await trpcClient.patient.familyMemberHistory.list.query({
        medplumPatientId: patient.medplumId,
        options: listOptions,
        offset,
        count,
      });
      return { items: res.familyMemberHistories, hasMore: res.hasMore };
    },
  });

  const { mutateAsync: createFamilyMemberHistory } = useMutation(
    trpc.patient.familyMemberHistory.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.familyMemberHistory.list.queryKey({ medplumPatientId: patient.medplumId }),
        });
      },
    }),
  );

  const { promote, getPromotionStatus } = useRecordPromotionStatus();

  const handlePromotionClick = useCallback(
    (id: string): void => {
      const source = familyMemberHistories.find((h) => h.id === id);
      if (!source) return;

      void promote(id, async () =>
        createFamilyMemberHistory({
          resource: {
            ...source,
            id: undefined,
            date: new Date().toISOString(),
            patient: { reference: `Patient/${patient.medplumId}` },
          },
        }),
      );
    },
    [createFamilyMemberHistory, familyMemberHistories, patient, promote],
  );

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('familyHistory')}
        searchPlaceholder={t('searchFamilyHistory')}
        onFiltersChange={handleFiltersChange}
        onAdd={() => setIsAddOpen(true)}
        addLabel={t('addFamilyMemberHistory')}
      />
      <AddFamilyMemberHistoryDialog
        medplumPatientId={patient.medplumId}
        isOpen={isAddOpen}
        onOpenChange={setIsAddOpen}
      />
      <RecordListScroll isLoading={!query.isError && !isComplete} isError={query.isError} error={query.error}>
        <FamilyMemberHistoryList
          familyMemberHistories={familyMemberHistories}
          onSelectFamilyMember={(id) => onViewChange({ type: 'family-member-detail', id })}
          selectedFamilyMemberId={selectedId}
          onPromotionClick={filters.ownership === 'external' ? handlePromotionClick : undefined}
          getPromotionStatus={getPromotionStatus}
        />
      </RecordListScroll>
    </div>
  );
};
